import { memo } from "react";
import { makeStyles } from "tss-react/mui";
import { MiButton } from "@miplatform/components";
import {
  BUTTON_COLORS,
  BUTTON_VARIANTS,
  POPPER_DELAY,
} from "@miplatform/constants";
import { mdiInformationSlabCircleOutline } from "@mdi/js";
import { Box, Tooltip } from "@mui/material";
import { OrderInfoCard } from "../OrderInfoCard/OrderInfoCard";

/**
 * A component that displays a button with a hoverable tooltip containing order information.
 *
 * @param {Object} props - Component props.
 * @param {Object} props.order - The order object containing information to be displayed in the tooltip.
 * @param {Object} props.fieldsConfig - The config object containing fields to be displayed.
 *
 * @returns {JSX.Element} The rendered component with a button and a hoverable tooltip.
 *
 * @example
 * <OrderInfoPopper order={order} />
 */
export const OrderInfoPopper = memo(({ hideEmails, order, fieldsConfig }) => {
  const { classes } = useStyles();

  return (
    <Tooltip
      data-testid="order-info-tooltip"
      enterDelay={POPPER_DELAY}
      title={
        <OrderInfoCard
          hideEmails={hideEmails}
          order={order}
          fieldsConfig={fieldsConfig}
        />
      }
      slotProps={{
        popper: {
          placement: "right",
          modifiers: [
            {
              name: "offset",
              options: { offset: [0, -18] },
            },
          ],
          sx: { p: 0, m: 0 },
        },
        tooltip: { sx: { p: 0, m: 0 } },
      }}
    >
      <Box>
        <MiButton
          size="small"
          className={classes.infoButton}
          variant={BUTTON_VARIANTS.CONTAINED}
          color={BUTTON_COLORS.PRIMARY}
          startIcon={{ path: mdiInformationSlabCircleOutline, iconSize: 0.5 }}
        />
      </Box>
    </Tooltip>
  );
});

const useStyles = makeStyles()((theme) => ({
  infoButton: {
    backgroundColor: theme.palette.primary.main,
    padding: theme.spacing(0.3, 1.5),
    marginLeft: theme.spacing(1),
  },
}));
